import { KeyboardEvent, KeyboardEventHandler, useRef, useState } from "react";
import styles from "./InputFilter.module.scss";
import { useAtomValue, useSetAtom } from "jotai";
import { Box, InputAdornment, OutlinedInput, Select, MenuItem, Typography, Divider, Chip, Card, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { newsAutocompleteState, dividerState, greyLighterState, themeState, backgroundColorState } from "@/lib/store";
import { findInstruments } from "@/controllers/find-instruments";
import { useDebounce } from "react-use";
import { DataGridHeaders } from "@/models/datagrid";
import Autocomplete from "../autocomplete/Autocomplete";

type Props = {
    fields: Array<any>;
    setParams: (params: any) => void;
    values?: Array<any>;
    chips: Array<any>;
    setChips: React.Dispatch<React.SetStateAction<any[]>>;
    isNewsSearch?: boolean;
    stockColumns?: Array<DataGridHeaders>;
    initialParams?: any[];
};

let searchTimeout: NodeJS.Timeout;

const InputFilter = ({ fields, setParams, values, chips, setChips, isNewsSearch, stockColumns, initialParams }: Props) => {
    const theme = useAtomValue(themeState);
    const greyLighter = useAtomValue(greyLighterState);
    const dividerColor = useAtomValue(dividerState);
    const backgroundColor = useAtomValue(backgroundColorState);

    const [searchValue, setSearchValue] = useState("");
    const [selectValue, setSelectValue] = useState(fields.find((field) => field.field !== "tradingitemId").headerName);
    const setNewsAutocomplete = useSetAtom(newsAutocompleteState);
    const newsAutocomplete = useAtomValue(newsAutocompleteState);

    const [selectedField, setSelectedField] = useState(fields.find((field) => field.headerName === selectValue));

    const timeout = useRef<NodeJS.Timeout>(undefined);

    const stockColumnDefinition = stockColumns?.[0]?.field;
    // Responses are camelCase and these checks were written in snake_case, so
    // `tradingitemId` matched none of them and the picker fell through to
    // sending the instrument's own id under whatever column it had bound to.
    const same = (field: string | undefined, name: string) => (field || "").replace(/_/g, "").toLowerCase() === name;
    const byTradingItem = same(stockColumnDefinition, "tradingitemid");
    const byCompany = same(stockColumnDefinition, "companyid");

    const onDelete = (chip) => {
        const chipValue = (c) => (byTradingItem ? c.tradingItemId : isNewsSearch ? (c.companyId ?? c.value) : byCompany ? c.companyId : c.value);
        const filteredValue = chipValue(chip);
        const newChips = chips.filter((c) => chipValue(c) !== filteredValue);
        setChips(newChips);
        setParams((prevParams) => {
            const newParams = prevParams.customFilterModel?.items.filter((item) => item.value !== filteredValue) || [];
            return {
                ...prevParams,
                customFilterModel: {
                    items: [...newParams],
                },
            };
        });
    };

    const onClick = (instrument) => {
        const newChip = instrument
            ? {
                  value: isNewsSearch ? instrument.company_id : instrument.value,
                  headerName: selectValue,
                  stockName: instrument?.ticker,
                  tradingItemId: instrument.tradingitem_id,
                  companyId: instrument.company_id,
              }
            : { value: searchValue, headerName: selectValue };

        setChips((prevChips) => [...prevChips, newChip]);

        instrument && setNewsAutocomplete({ onClick, data: [] });

        clearTimeout(timeout.current);
        timeout.current = setTimeout(() => {
            const formattedValue = {
                columnField: instrument && !isNewsSearch && stockColumnDefinition ? stockColumnDefinition : selectedField.field,
                id: Date.now(),
                operatorValue: instrument ? "equals" : "contains",
                value: instrument ? (isNewsSearch || byCompany ? instrument.company_id : byTradingItem ? instrument.tradingitem_id : instrument.value) : searchValue,
            };

            setParams((prevParams) => {
                const oldParams = prevParams?.customFilterModel?.items || [];
                return {
                    ...prevParams,
                    customFilterModel: {
                        items: [...oldParams, formattedValue],
                    },
                };
            });
            setSearchValue("");
        }, 1000);
    };

    const onChangeSelect = ({ target: { value } }) => {
        setSelectValue(value);
        setSelectedField(fields.find((field) => field.headerName === value));
    };

    const setChipBgColor = (headerName: string) => {
        const colorMap = {
            source: "#faebd7",
            stock: "#8fbc8f",
        };
        return colorMap[headerName.toLowerCase()] || (theme !== "dark" ? dividerColor : "#e7e7e8");
    };

    const getInstruments = (event: KeyboardEvent<HTMLInputElement>) => {
        setNewsAutocomplete({ onClick, loading: true, data: [] });
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
            findInstruments(event.target["value"]).then((data) => {
                setNewsAutocomplete({ onClick, loading: false, data });
            });
        }, 500);
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
        if (event.key !== "Enter") {
            return;
        }

        if (selectedField.format !== "company" && selectedField.format !== "stock_list" && selectedField.format !== "company_w_logo") {
            event.preventDefault();
            event.stopPropagation();
            searchValue && onClick(null);
        }
    };

    const handleOnChange = (event) => {
        setSearchValue(event.target.value);
    };

    useDebounce(
        () => {
            if (selectedField.format === "company" || selectedField.format === "stock_list" || selectedField.format === "company_w_logo") {
                if (searchValue) {
                    getInstruments({ target: { value: searchValue } } as any);
                } else {
                    setNewsAutocomplete({ onClick, loading: false, data: [] });
                }
            }
        },
        500,
        [searchValue]
    );

    const handleInputBlur = () => {
        setTimeout(() => {
            setSearchValue("");
            setNewsAutocomplete({ onClick, loading: false, data: [] });
        }, 300);
    };

    return (
        <>
            <Typography variant="subtitle1" sx={{ fontSize: "14px", fontWeight: "700", textAlign: "left", width: "100%" }}>
                Filter by
            </Typography>
            <Divider flexItem sx={{ mb: 0.5, backgroundColor: dividerColor }} />
            <Box className={styles.form} sx={{ display: "flex", flexDirection: "row !important", mt: 2 }}>
                <OutlinedInput
                    autoCapitalize="none"
                    autoComplete="off"
                    className={styles.input}
                    id="outlined-adornment-amount"
                    size="small"
                    value={searchValue}
                    onChange={handleOnChange}
                    endAdornment={
                        <div className={styles.adornment}>
                            <InputAdornment position="end" onClick={() => searchValue && onClick(null)}>
                                <AddIcon htmlColor={greyLighter} />
                            </InputAdornment>
                        </div>
                    }
                    placeholder="Search by..."
                    name="search"
                    onKeyDown={handleKeyDown}
                    onBlur={handleInputBlur}
                />
                <Select className={styles.select} onChange={onChangeSelect} size="small" value={selectValue} displayEmpty renderValue={(value) => (value === "" ? "Select an option" : value)}>
                    {fields
                        .filter((field) => field.headerName)
                        .map((field) => (
                            <MenuItem value={field.headerName} sx={{ fontSize: "12px", textTransform: "capitalize" }} key={field.headerName}>
                                {field.headerName}
                            </MenuItem>
                        ))}
                </Select>
            </Box>
            <Box className={styles.chips}>
                {chips.map((chip) => (
                    <Chip
                        className={styles.chip}
                        label={chip.stockName || chip.value}
                        onDelete={() => onDelete(chip)}
                        key={`${chip.headerName} ${chip.value}`}
                        size="small"
                        sx={{
                            backgroundColor: setChipBgColor(chip.headerName),
                            color: theme === "dark" && "#000",
                            "& .MuiChip-deleteIcon": {
                                color: theme === "dark" && "grey",
                                ":hover": {
                                    color: "#ef4438ed",
                                },
                            },
                        }}
                    />
                ))}
            </Box>

            <Box sx={{ position: "absolute", zIndex: 10, width: "95%", backgroundColor: backgroundColor }}>
                {newsAutocomplete?.loading && (
                    <Card style={{ width: "100%", height: "250px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CircularProgress />
                    </Card>
                )}
                {newsAutocomplete?.data?.length > 0 && (
                    <Card>
                        <Autocomplete instruments={newsAutocomplete?.data} onClick={newsAutocomplete?.onClick} />
                    </Card>
                )}
            </Box>
        </>
    );
};

export default InputFilter;
